/**
 * Budget Screen
 * Budget overview with date-range picker, account filter, transactions
 * (Gmail-style multi-select), Qonto-style pagination, and budget limits (plafonds).
 */

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  SafeAreaView,
  ActivityIndicator,
  RefreshControl,
  TouchableOpacity,
  TextInput,
  Alert,
  Platform,
  Switch,
  Modal,
} from 'react-native';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { format, parseISO, isToday, isYesterday, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';
import { formatCurrency } from '../utils/formatters';
import { useBudgetStore } from '../store/budgetStore';
import { useLayout } from '../components/layout/AppLayout';
import { budgetApi } from '../api/endpoints/budget';
import AddTransactionModal from '../components/budget/AddTransactionModal';
import EditBudgetLimitModal from '../components/budget/EditBudgetLimitModal';
import BudgetLimitProgressCard from '../components/budget/BudgetLimitProgressCard';
import DateField from '../components/common/DateField';
import RecurringTab from '../components/budget/RecurringTab';
import type { Transaction, BudgetLimit, FinancialAccount } from '../api/types';
import logger from '../utils/logger';

type TabKey = 'transactions' | 'recurring' | 'limits';
type LimitKindFilter = 'all' | 'category' | 'tag';

const CATEGORY_LABELS: Record<string, string> = {
  housing: 'Logement',
  food: 'Alimentation',
  transport: 'Transport',
  insurance: 'Assurance',
  subscriptions: 'Abonnements',
  leisure: 'Loisirs',
  health: 'Santé',
  taxes: 'Impôts',
  education: 'Éducation',
  family_assistance: 'Aide familiale',
  children_expenses: 'Enfants',
  debt_repayment: 'Crédit',
  shopping: 'Shopping',
  savings: 'Épargne',
  salary: 'Salaire',
  rental_income: 'Revenus locatifs',
  investment_income: 'Revenus placements',
  freelance_income: 'Revenus freelance',
  other_income: 'Autres revenus',
  bank_fees: 'Frais bancaires',
  other: 'Autre',
};

const CATEGORY_ICONS: Record<string, string> = {
  housing: 'home',
  food: 'food',
  transport: 'car',
  insurance: 'shield-check',
  subscriptions: 'cellphone',
  leisure: 'gamepad-variant',
  health: 'heart-pulse',
  taxes: 'file-document',
  education: 'school',
  family_assistance: 'account-group',
  children_expenses: 'baby-carriage',
  debt_repayment: 'bank-transfer',
  shopping: 'shopping',
  savings: 'piggy-bank',
  salary: 'cash',
  business: 'briefcase',
  investment: 'chart-line',
  rental: 'home-city',
  bank_fees: 'alert-circle-outline',
  other: 'dots-horizontal',
};

const TYPE_FILTER_OPTIONS = [
  { key: 'all' as const, label: 'Toutes' },
  { key: 'expense' as const, label: 'Dépenses' },
  { key: 'income' as const, label: 'Revenus' },
];

const PER_PAGE_OPTIONS = [25, 50, 100];

// Format account display name: "Institution - Account Name ****XXXX"
const formatAccountDisplayName = (account: FinancialAccount): string => {
  const parts: string[] = [];
  if (account.institution_name) parts.push(account.institution_name);
  parts.push(account.name);
  if (account.iban_masked) {
    const lastFour = account.iban_masked.slice(-4);
    if (lastFour && !account.name.includes(lastFour)) {
      parts[parts.length - 1] += ` ****${lastFour}`;
    }
  }
  return parts.join(' - ');
};

const isValidDateString = (s: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(s);

export default function BudgetScreen() {
  const isFocused = useIsFocused();
  const navigation = useNavigation();
  const { navigateTo, navigationParams, clearNavigationParams } = useLayout();

  const {
    transactions,
    budgetLimits,
    summary,
    dateRange,
    isLoading,
    error,
    filterType,
    filterTags,
    showExcluded,
    page,
    perPage,
    totalCount,
    loadTransactions,
    loadBudgetLimits,
    loadSummary,
    setDateRange,
    setFilterType,
    toggleFilterTag,
    setSearchQuery,
    setShowExcluded,
    setPage,
    setPerPage,
    refresh,
    deleteBudgetLimit,
    deleteTransaction,
    recurringTransactions,
    recurringLoading,
    loadRecurring,
  } = useBudgetStore();

  const [activeTab, setActiveTab] = useState<TabKey>('transactions');
  const [refreshing, setRefreshing] = useState(false);
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [showEditLimit, setShowEditLimit] = useState(false);
  const [selectedLimit, setSelectedLimit] = useState<BudgetLimit | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [localSearch, setLocalSearch] = useState('');
  const [showTypeFilter, setShowTypeFilter] = useState(false);
  const [showAccountFilter, setShowAccountFilter] = useState(false);
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
  const [userTags, setUserTags] = useState<Array<{ name: string; color: string | null; usage_count: number }>>([]);
  const [selectedTxIds, setSelectedTxIds] = useState<Set<string>>(new Set());
  const [limitKindFilter, setLimitKindFilter] = useState<LimitKindFilter>('all');

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load accounts
  const loadAccounts = async () => {
    try {
      const data = await budgetApi.getFinancialAccounts();
      setAccounts(data);
    } catch (err) {
      logger.error('[BudgetScreen] Error loading accounts:', err);
    }
  };

  // Load user tags
  const loadUserTags = async () => {
    try {
      const tags = await budgetApi.getUserTags();
      setUserTags(tags);
    } catch (err) {
      logger.error('[BudgetScreen] Error loading user tags:', err);
    }
  };

  const loadAll = async () => {
    await Promise.all([
      loadTransactions(),
      loadBudgetLimits(),
      loadSummary(),
      loadAccounts(),
      loadUserTags(),
    ]);
    setInitialLoad(false);
  };

  // Load data when screen is focused
  useEffect(() => {
    if (isFocused) {
      loadAll();
    }
  }, [isFocused]);

  // Auto-open import wizard when navigated with openCsvImport param
  useEffect(() => {
    if (isFocused && navigationParams?.openCsvImport) {
      clearNavigationParams();
      (navigation as any).navigate('ImportBankStatementsWizard');
    }
  }, [isFocused, navigationParams?.openCsvImport]);

  const onRefresh = async () => {
    setRefreshing(true);
    await Promise.all([refresh(), loadAccounts(), loadUserTags()]);
    setRefreshing(false);
  };

  // Debounced search
  const handleSearchChange = useCallback((text: string) => {
    setLocalSearch(text);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchQuery(text);
    }, 400);
  }, [setSearchQuery]);

  // ============================================================
  // Date range
  // ============================================================
  const handleStartChange = (value: string) => {
    if (!isValidDateString(value)) return;
    // Auto-correct if start > end
    const newEnd = value > dateRange.end ? value : dateRange.end;
    setDateRange(value, newEnd);
  };

  const handleEndChange = (value: string) => {
    if (!isValidDateString(value)) return;
    const newStart = value < dateRange.start ? value : dateRange.start;
    setDateRange(newStart, value);
  };

  // Days remaining in the period (for the summary card)
  const daysRemaining = useMemo(() => {
    try {
      const end = parseISO(dateRange.end);
      const today = new Date();
      return Math.max(0, differenceInDays(end, today));
    } catch {
      return 0;
    }
  }, [dateRange.end]);

  const periodLabel = useMemo(() => {
    try {
      const start = parseISO(dateRange.start);
      const end = parseISO(dateRange.end);
      const sameMonth =
        start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
      if (sameMonth) {
        const monthLabel = format(start, 'MMMM yyyy', { locale: fr });
        return monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);
      }
      return `${format(start, 'd MMM yyyy', { locale: fr })} → ${format(end, 'd MMM yyyy', { locale: fr })}`;
    } catch {
      return '';
    }
  }, [dateRange.start, dateRange.end]);

  // ============================================================
  // Limit (plafond) actions
  // ============================================================
  const handleEditLimit = (bl: BudgetLimit) => {
    setSelectedLimit(bl);
    setShowEditLimit(true);
  };

  const handleAddLimit = () => {
    setSelectedLimit(null);
    setShowEditLimit(true);
  };

  const handleDeleteLimit = (bl: BudgetLimit) => {
    const doDelete = () => {
      deleteBudgetLimit(bl.id).catch((err) => {
        logger.error('[BudgetScreen] Delete error:', err);
        Alert.alert('Erreur', 'Impossible de supprimer ce plafond');
      });
    };
    if (typeof window !== 'undefined' && window.confirm) {
      if (window.confirm(`Supprimer le plafond "${bl.name}" ?`)) doDelete();
    } else {
      Alert.alert(
        'Supprimer',
        `Supprimer le plafond "${bl.name}" ?`,
        [
          { text: 'Annuler', style: 'cancel' },
          { text: 'Supprimer', style: 'destructive', onPress: doDelete },
        ],
      );
    }
  };

  // ============================================================
  // Transaction actions
  // ============================================================
  const handleEditTransaction = (tx: Transaction) => {
    setEditingTransaction(tx);
    setShowAddTransaction(true);
  };

  const doDeleteTransaction = async (tx: Transaction) => {
    try {
      await deleteTransaction(tx.id);
      loadTransactions();
      loadSummary();
    } catch {
      if (Platform.OS === 'web') {
        window.alert('Impossible de supprimer cette transaction.');
      } else {
        Alert.alert('Erreur', 'Impossible de supprimer cette transaction.');
      }
    }
  };

  const handleLongPressTransaction = (tx: Transaction) => {
    if (Platform.OS === 'web') {
      if (window.confirm(`Supprimer "${tx.description}" ?`)) doDeleteTransaction(tx);
    } else {
      Alert.alert(
        tx.description,
        undefined,
        [
          { text: 'Modifier', onPress: () => handleEditTransaction(tx) },
          { text: 'Supprimer', style: 'destructive', onPress: () => doDeleteTransaction(tx) },
          { text: 'Annuler', style: 'cancel' },
        ],
      );
    }
  };

  const handleCloseTransactionModal = () => {
    setShowAddTransaction(false);
    setEditingTransaction(null);
    loadTransactions();
    loadSummary();
  };

  // Format transaction date for grouping with relative labels
  const formatTransactionDate = (dateStr: string): string => {
    try {
      const date = parseISO(dateStr);
      if (isToday(date)) return "Aujourd'hui";
      if (isYesterday(date)) return 'Hier';
      const formatted = format(date, 'EEEE d MMMM', { locale: fr });
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    } catch {
      return dateStr;
    }
  };

  // Filter transactions by account and category (client-side)
  const filteredTransactions = transactions.filter((tx) => {
    if (selectedAccountId && tx.financial_account_id !== selectedAccountId) return false;
    if (selectedCategoryFilter && tx.category !== selectedCategoryFilter) return false;
    return true;
  });

  // Filter budget limits by kind (client-side)
  const filteredLimits = budgetLimits.filter(
    (bl) => limitKindFilter === 'all' || bl.kind === limitKindFilter,
  );

  // Subtotals — always shown; the period itself is considered an active filter,
  // so any change to Du/Au updates the counter and totals.
  const filteredSubtotals = {
    count: filteredTransactions.length,
    expenses: filteredTransactions
      .filter((tx) => tx.transaction_type === 'expense')
      .reduce((sum, tx) => sum + Math.abs(tx.amount), 0),
    income: filteredTransactions
      .filter((tx) => tx.transaction_type === 'income')
      .reduce((sum, tx) => sum + Math.abs(tx.amount), 0),
  };

  const selectedTxList = filteredTransactions.filter((tx) => selectedTxIds.has(tx.id));
  const selectionTotals =
    selectedTxList.length > 0
      ? {
          count: selectedTxList.length,
          expenses: selectedTxList
            .filter((tx) => tx.transaction_type === 'expense')
            .reduce((sum, tx) => sum + Math.abs(tx.amount), 0),
          income: selectedTxList
            .filter((tx) => tx.transaction_type === 'income')
            .reduce((sum, tx) => sum + Math.abs(tx.amount), 0),
        }
      : null;

  const toggleSelected = (id: string) => {
    setSelectedTxIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedTxIds(new Set());

  // Reset selection whenever the filtered set changes meaningfully
  useEffect(() => {
    if (selectedTxIds.size === 0) return;
    const validIds = new Set(filteredTransactions.map((tx) => tx.id));
    let changed = false;
    const next = new Set<string>();
    selectedTxIds.forEach((id) => {
      if (validIds.has(id)) next.add(id);
      else changed = true;
    });
    if (changed) setSelectedTxIds(next);
  }, [filteredTransactions, selectedTxIds]);

  // Group transactions by date
  const groupedTransactions = filteredTransactions.reduce<Record<string, Transaction[]>>(
    (groups, tx) => {
      const dateKey = tx.transaction_date;
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(tx);
      return groups;
    },
    {},
  );
  const sortedDates = Object.keys(groupedTransactions).sort((a, b) => b.localeCompare(a));

  const activeFilterLabel =
    TYPE_FILTER_OPTIONS.find((o) => o.key === filterType)?.label || 'Toutes';
  const selectedAccount = accounts.find((a) => a.id === selectedAccountId);

  // Get unique categories from transactions for filter
  const availableCategories = [...new Set(transactions.map((tx) => tx.category))].sort();

  // ============================================================
  // Pagination
  // ============================================================
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = Math.min(page * perPage, totalCount);
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  // Loading state for initial load
  if (initialLoad && isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Chargement du budget...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >
        {/* Budget Summary Card */}
        {summary && (
          <View style={styles.summaryCard}>
            <View style={styles.summaryHeader}>
              <Icon name="chart-arc" size={20} color={colors.primary} />
              <Text style={styles.summaryTitle}>{periodLabel}</Text>
            </View>

            <View style={styles.summaryStats}>
              <Text style={styles.summaryBudgetLabel}>
                Budget total : {formatCurrency(summary.total_income)}
              </Text>
              <Text style={styles.summarySpentLabel}>
                Dépensé : {formatCurrency(summary.total_expenses)} (
                {summary.total_income > 0
                  ? Math.round((summary.total_expenses / summary.total_income) * 100)
                  : 0}
                %)
              </Text>
            </View>

            {summary.total_income > 0 && (
              <View style={styles.progressContainer}>
                <View style={styles.progressBar}>
                  <View
                    style={[
                      styles.progressFill,
                      {
                        width: `${Math.min((summary.total_expenses / summary.total_income) * 100, 100)}%`,
                        backgroundColor:
                          summary.total_expenses > summary.total_income
                            ? colors.error
                            : summary.total_expenses > summary.total_income * 0.9
                              ? colors.warning
                              : colors.primary,
                      },
                    ]}
                  />
                </View>
                <Text style={styles.progressText}>
                  Reste : {formatCurrency(Math.max(0, summary.total_income - summary.total_expenses))}{' '}
                  pour {daysRemaining} jours
                </Text>
              </View>
            )}

            <View style={styles.summaryActions}>
              <TouchableOpacity
                style={styles.summaryActionButton}
                onPress={() => navigateTo('Chats')}
              >
                <Icon name="chart-line" size={16} color={colors.primary} />
                <Text style={styles.summaryActionText}>Voir ma progression</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.summaryActionButton}
                onPress={() => navigateTo('Chats')}
              >
                <Icon name="lightbulb-outline" size={16} color={colors.primary} />
                <Text style={styles.summaryActionText}>Smart Suggest</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* Filters Section */}
        <View style={styles.filtersCard}>
          <Text style={styles.filtersSectionTitle}>Filtres</Text>

          {/* Period filter (date range) */}
          <View style={styles.filterRow}>
            <Icon name="calendar-range" size={18} color={colors.textSecondary} />
            <Text style={styles.filterLabel}>Période</Text>
            {!!periodLabel && <Text style={styles.filterLabelSmall}>· {periodLabel}</Text>}
          </View>
          <View style={styles.periodInputs}>
            <DateField label="Du" value={dateRange.start} onChange={handleStartChange} />
            <DateField label="Au" value={dateRange.end} onChange={handleEndChange} />
          </View>

          {/* Account filter */}
          <View style={[styles.filterRow, { marginTop: spacing.md }]}>
            <Icon name="bank" size={18} color={colors.textSecondary} />
            <Text style={styles.filterLabel}>Compte</Text>
          </View>
          <View style={styles.accountFilterWrapper}>
            <TouchableOpacity
              style={styles.filterSelect}
              onPress={() => {
                setShowAccountFilter(!showAccountFilter);
                setShowTypeFilter(false);
                setShowCategoryFilter(false);
              }}
            >
              <Text style={styles.filterSelectText} numberOfLines={1}>
                {selectedAccount ? formatAccountDisplayName(selectedAccount) : 'Tous les comptes'}
              </Text>
              <Icon
                name={showAccountFilter ? 'chevron-up' : 'chevron-down'}
                size={18}
                color={colors.textSecondary}
              />
            </TouchableOpacity>
            {showAccountFilter && (
              <ScrollView style={styles.filterDropdown} nestedScrollEnabled>
                <TouchableOpacity
                  style={[styles.filterOption, !selectedAccountId && styles.filterOptionActive]}
                  onPress={() => {
                    setSelectedAccountId(null);
                    setShowAccountFilter(false);
                  }}
                >
                  <Text
                    style={[
                      styles.filterOptionText,
                      !selectedAccountId && styles.filterOptionTextActive,
                    ]}
                  >
                    Tous les comptes
                  </Text>
                  {!selectedAccountId && <Icon name="check" size={16} color={colors.primary} />}
                </TouchableOpacity>
                {accounts.map((account) => (
                  <TouchableOpacity
                    key={account.id}
                    style={[
                      styles.filterOption,
                      selectedAccountId === account.id && styles.filterOptionActive,
                    ]}
                    onPress={() => {
                      setSelectedAccountId(account.id);
                      setShowAccountFilter(false);
                    }}
                  >
                    <View style={styles.filterOptionContent}>
                      <Text
                        style={[
                          styles.filterOptionText,
                          selectedAccountId === account.id && styles.filterOptionTextActive,
                        ]}
                        numberOfLines={1}
                      >
                        {formatAccountDisplayName(account)}
                      </Text>
                      <Text style={styles.filterOptionSubtext}>
                        {formatCurrency(account.balance)}
                      </Text>
                    </View>
                    {selectedAccountId === account.id && (
                      <Icon name="check" size={16} color={colors.primary} />
                    )}
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}
          </View>

          {/* Type filter + Category filter */}
          <View style={styles.filterRowDouble}>
            <View style={[styles.filterHalf, showTypeFilter && styles.filterHalfOpen]}>
              <View style={styles.filterRow}>
                <Icon name="swap-vertical" size={16} color={colors.textSecondary} />
                <Text style={styles.filterLabelSmall}>Type de Transaction</Text>
              </View>
              <View style={styles.selectWrapper}>
                <TouchableOpacity
                  style={styles.filterSelectSmall}
                  onPress={() => {
                    setShowTypeFilter(!showTypeFilter);
                    setShowAccountFilter(false);
                    setShowCategoryFilter(false);
                  }}
                >
                  <Text style={styles.filterSelectTextSmall}>{activeFilterLabel}</Text>
                  <Icon
                    name={showTypeFilter ? 'chevron-up' : 'chevron-down'}
                    size={16}
                    color={colors.textSecondary}
                  />
                </TouchableOpacity>
                {showTypeFilter && (
                  <ScrollView style={styles.filterDropdownSmall} nestedScrollEnabled>
                    {TYPE_FILTER_OPTIONS.map((option) => (
                      <TouchableOpacity
                        key={option.key}
                        style={[
                          styles.filterOption,
                          filterType === option.key && styles.filterOptionActive,
                        ]}
                        onPress={() => {
                          setFilterType(option.key);
                          setShowTypeFilter(false);
                        }}
                      >
                        <Text
                          style={[
                            styles.filterOptionText,
                            filterType === option.key && styles.filterOptionTextActive,
                          ]}
                        >
                          {option.label}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                )}
              </View>
            </View>

            <View style={[styles.filterHalf, showCategoryFilter && styles.filterHalfOpen]}>
              <View style={styles.filterRow}>
                <Icon name="folder" size={16} color={colors.textSecondary} />
                <Text style={styles.filterLabelSmall}>Catégorie</Text>
              </View>
              <View style={styles.selectWrapper}>
                <TouchableOpacity
                  style={styles.filterSelectSmall}
                  onPress={() => {
                    setShowCategoryFilter(!showCategoryFilter);
                    setShowAccountFilter(false);
                    setShowTypeFilter(false);
                  }}
                >
                  <Text style={styles.filterSelectTextSmall} numberOfLines={1}>
                    {selectedCategoryFilter
                      ? CATEGORY_LABELS[selectedCategoryFilter] || selectedCategoryFilter
                      : 'Toutes'}
                  </Text>
                  <Icon
                    name={showCategoryFilter ? 'chevron-up' : 'chevron-down'}
                    size={16}
                    color={colors.textSecondary}
                  />
                </TouchableOpacity>
                {showCategoryFilter && (
                  <ScrollView style={styles.filterDropdownSmall} nestedScrollEnabled>
                    <TouchableOpacity
                      style={[
                        styles.filterOption,
                        !selectedCategoryFilter && styles.filterOptionActive,
                      ]}
                      onPress={() => {
                        setSelectedCategoryFilter(null);
                        setShowCategoryFilter(false);
                      }}
                    >
                      <Text
                        style={[
                          styles.filterOptionText,
                          !selectedCategoryFilter && styles.filterOptionTextActive,
                        ]}
                      >
                        Toutes
                      </Text>
                    </TouchableOpacity>
                    {availableCategories.map((cat) => (
                      <TouchableOpacity
                        key={cat}
                        style={[
                          styles.filterOption,
                          selectedCategoryFilter === cat && styles.filterOptionActive,
                        ]}
                        onPress={() => {
                          setSelectedCategoryFilter(cat);
                          setShowCategoryFilter(false);
                        }}
                      >
                        <Text
                          style={[
                            styles.filterOptionText,
                            selectedCategoryFilter === cat && styles.filterOptionTextActive,
                          ]}
                        >
                          {CATEGORY_LABELS[cat] || cat}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                )}
              </View>
            </View>
          </View>

          {/* Tag filter — placed above the "Inclure exclues" toggle */}
          {userTags.length > 0 && !showTypeFilter && !showCategoryFilter && (
            <>
              <View style={styles.filterRow}>
                <Icon name="tag-multiple" size={18} color={colors.textSecondary} />
                <Text style={styles.filterLabel}>Tags</Text>
              </View>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                style={styles.tagFilterScroll}
                contentContainerStyle={styles.tagFilterContent}
              >
                {userTags.map((tag) => {
                  const isActive = filterTags.includes(tag.name);
                  return (
                    <TouchableOpacity
                      key={tag.name}
                      style={[
                        styles.tagChip,
                        isActive && styles.tagChipActive,
                        tag.color && isActive ? { backgroundColor: tag.color } : null,
                      ]}
                      onPress={() => toggleFilterTag(tag.name)}
                    >
                      <Text style={[styles.tagChipText, isActive && styles.tagChipTextActive]}>
                        {tag.name}
                      </Text>
                      {isActive && (
                        <Icon name="close" size={14} color="#fff" style={{ marginLeft: 4 }} />
                      )}
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </>
          )}

          {/* Excluded toggle */}
          {!showTypeFilter && !showCategoryFilter && (
            <View style={styles.excludedRow}>
              <View style={styles.excludedLabelRow}>
                <Icon name="eye-off-outline" size={18} color={colors.textSecondary} />
                <Text style={styles.filterLabel}>Inclure les transactions exclues</Text>
              </View>
              <Switch
                value={showExcluded}
                onValueChange={setShowExcluded}
                trackColor={{ false: colors.border, true: colors.primary + '80' }}
                thumbColor={showExcluded ? colors.primary : '#f4f3f4'}
              />
            </View>
          )}

          {/* Search */}
          {!showTypeFilter && !showCategoryFilter && (
            <>
              <View style={styles.filterRow}>
                <Icon name="magnify" size={18} color={colors.textSecondary} />
                <Text style={styles.filterLabel}>Recherche</Text>
              </View>
              <View style={styles.searchContainer}>
                <TextInput
                  style={styles.searchInput}
                  value={localSearch}
                  onChangeText={handleSearchChange}
                  placeholder="Rechercher une transaction..."
                  placeholderTextColor={colors.textSecondary}
                  returnKeyType="search"
                />
                {localSearch.length > 0 && (
                  <TouchableOpacity onPress={() => handleSearchChange('')}>
                    <Icon name="close-circle" size={18} color={colors.textSecondary} />
                  </TouchableOpacity>
                )}
              </View>
            </>
          )}
        </View>

        {/* Tab Selector */}
        <View style={styles.tabsContainer}>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'transactions' && styles.activeTab]}
            onPress={() => setActiveTab('transactions')}
          >
            <Text style={[styles.tabText, activeTab === 'transactions' && styles.activeTabText]}>
              Transactions
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'recurring' && styles.activeTab]}
            onPress={() => {
              setActiveTab('recurring');
              loadRecurring();
            }}
          >
            <Text style={[styles.tabText, activeTab === 'recurring' && styles.activeTabText]}>
              Abos
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'limits' && styles.activeTab]}
            onPress={() => setActiveTab('limits')}
          >
            <Text style={[styles.tabText, activeTab === 'limits' && styles.activeTabText]}>
              Plafonds
            </Text>
          </TouchableOpacity>
        </View>

        {/* Error banner */}
        {error && (
          <View style={styles.errorBanner}>
            <Icon name="alert-circle" size={18} color={colors.error} />
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        {/* Tab Content */}
        {activeTab === 'recurring' ? (
          <View style={styles.tabContent}>
            <RecurringTab
              transactions={recurringTransactions}
              isLoading={recurringLoading}
            />
          </View>
        ) : activeTab === 'transactions' ? (
          <View style={styles.tabContent}>
            {/* Transactions Header — action buttons (count is shown in totals bar below) */}
            <View style={styles.transactionsHeader}>
              <View style={styles.transactionsHeaderActions}>
                <TouchableOpacity
                  style={styles.headerActionButton}
                  onPress={() => {
                    setEditingTransaction(null);
                    setShowAddTransaction(true);
                  }}
                >
                  <Icon name="plus" size={16} color={colors.primary} />
                  <Text style={styles.headerActionText}>Ajouter manuel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.headerActionButton}
                  onPress={() => (navigation as any).navigate('ImportBankStatementsWizard')}
                >
                  <Icon name="file-upload-outline" size={16} color={colors.primary} />
                  <Text style={styles.headerActionText}>Importer relevé</Text>
                </TouchableOpacity>
              </View>
            </View>

            {/* Selected account indicator */}
            {selectedAccountId && selectedAccount && (
              <View style={styles.selectedAccountBadge}>
                <Text style={styles.selectedAccountText}>
                  Compte sélectionné : {selectedAccount.name}
                </Text>
                <TouchableOpacity onPress={() => setSelectedAccountId(null)}>
                  <Icon name="close-circle" size={16} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>
            )}

            {/* Subtotals — selection-aware. Order: Revenus, Dépenses, = Net */}
            {selectionTotals ? (
              <View style={styles.subtotalBar}>
                <Text style={styles.subtotalText}>
                  {selectionTotals.count} sélectionnée{selectionTotals.count > 1 ? 's' : ''} ·{' '}
                  <Text style={styles.subtotalIncome}>+{formatCurrency(selectionTotals.income)}</Text>
                  {' · '}
                  <Text style={styles.subtotalExpense}>-{formatCurrency(selectionTotals.expenses)}</Text>
                  {' = '}
                  <Text
                    style={
                      selectionTotals.income - selectionTotals.expenses >= 0
                        ? styles.subtotalIncome
                        : styles.subtotalExpense
                    }
                  >
                    {selectionTotals.income - selectionTotals.expenses >= 0 ? '+' : ''}
                    {formatCurrency(selectionTotals.income - selectionTotals.expenses)}
                  </Text>
                </Text>
                <TouchableOpacity onPress={clearSelection}>
                  <Text style={styles.subtotalAction}>Tout désélectionner</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.subtotalBar}>
                <Text style={styles.subtotalText}>
                  {filteredSubtotals.count} transaction{filteredSubtotals.count > 1 ? 's' : ''} ·{' '}
                  <Text style={styles.subtotalIncome}>+{formatCurrency(filteredSubtotals.income)}</Text>
                  {' · '}
                  <Text style={styles.subtotalExpense}>-{formatCurrency(filteredSubtotals.expenses)}</Text>
                  {' = '}
                  <Text
                    style={
                      filteredSubtotals.income - filteredSubtotals.expenses >= 0
                        ? styles.subtotalIncome
                        : styles.subtotalExpense
                    }
                  >
                    {filteredSubtotals.income - filteredSubtotals.expenses >= 0 ? '+' : ''}
                    {formatCurrency(filteredSubtotals.income - filteredSubtotals.expenses)}
                  </Text>
                </Text>
              </View>
            )}

            {/* Transactions list */}
            {filteredTransactions.length === 0 && !isLoading ? (
              <View style={styles.emptyContainer}>
                <Icon name="receipt" size={64} color={colors.textSecondary} />
                <Text style={styles.emptyTitle}>Aucune transaction</Text>
                <Text style={styles.emptyText}>
                  {localSearch
                    ? 'Aucun résultat pour cette recherche.'
                    : 'Ajoute une transaction manuelle ou importe un relevé bancaire.'}
                </Text>
              </View>
            ) : (
              <View style={styles.transactionsContainer}>
                {sortedDates.map((dateKey) => (
                  <View key={dateKey} style={styles.dateGroup}>
                    <Text style={styles.dateGroupLabel}>{formatTransactionDate(dateKey)}</Text>
                    {groupedTransactions[dateKey].map((tx) => {
                      const checked = selectedTxIds.has(tx.id);
                      return (
                        <TouchableOpacity
                          key={tx.id}
                          style={[styles.transactionItem, checked && styles.transactionItemSelected]}
                          onPress={() => handleEditTransaction(tx)}
                          onLongPress={() => handleLongPressTransaction(tx)}
                          activeOpacity={0.7}
                        >
                          {/* Checkbox (Gmail-style multi-select) */}
                          <TouchableOpacity
                            onPress={(e) => {
                              e.stopPropagation();
                              toggleSelected(tx.id);
                            }}
                            hitSlop={{ top: 12, bottom: 12, left: 12, right: 8 }}
                            style={styles.checkboxContainer}
                          >
                            <Icon
                              name={checked ? 'checkbox-marked' : 'checkbox-blank-outline'}
                              size={22}
                              color={checked ? colors.primary : colors.textSecondary}
                            />
                          </TouchableOpacity>

                          <View
                            style={[
                              styles.transactionIcon,
                              {
                                backgroundColor:
                                  tx.transaction_type === 'income'
                                    ? colors.success + '20'
                                    : colors.error + '20',
                              },
                            ]}
                          >
                            <Icon
                              name={CATEGORY_ICONS[tx.category] || 'cash'}
                              size={20}
                              color={
                                tx.transaction_type === 'income' ? colors.success : colors.error
                              }
                            />
                          </View>
                          <View style={styles.transactionInfo}>
                            <Text style={styles.transactionDescription} numberOfLines={1}>
                              {tx.description}
                              {tx.excluded_from_budget && (
                                <Text style={styles.excludedBadge}> · exclu</Text>
                              )}
                            </Text>
                            <Text style={styles.transactionMeta} numberOfLines={1}>
                              {tx.category_label || CATEGORY_LABELS[tx.category] || tx.category}
                              {tx.financial_account_name && (
                                <Text style={styles.transactionAccount}>
                                  {' · '}
                                  {tx.financial_account_name}
                                  {tx.financial_account_iban_masked &&
                                    ` ${tx.financial_account_iban_masked}`}
                                </Text>
                              )}
                            </Text>
                          </View>
                          <Text
                            style={[
                              styles.transactionAmount,
                              tx.transaction_type === 'income'
                                ? styles.incomeValue
                                : styles.expenseValue,
                            ]}
                          >
                            {tx.transaction_type === 'income' ? '+' : '-'}
                            {formatCurrency(Math.abs(tx.amount))}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                ))}

                {/* Pagination footer — Qonto-exact layout */}
                {totalCount > 0 && (
                  <View style={styles.paginationBar}>
                    {/* Per-page button group + label */}
                    <View style={styles.perPageGroup}>
                      <View style={styles.perPageButtons}>
                        {PER_PAGE_OPTIONS.map((opt) => {
                          const active = perPage === opt;
                          return (
                            <TouchableOpacity
                              key={opt}
                              style={[styles.perPageButton, active && styles.perPageButtonActive]}
                              onPress={() => setPerPage(opt)}
                            >
                              <Text
                                style={[
                                  styles.perPageButtonText,
                                  active && styles.perPageButtonTextActive,
                                ]}
                              >
                                {opt}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                      <Text style={styles.perPageLabel}>Éléments par page</Text>
                    </View>

                    {/* Current page description */}
                    <Text style={styles.paginationInfo}>
                      {rangeStart}-{rangeEnd} sur {totalCount}
                    </Text>

                    {/* Prev / Next chevrons */}
                    <View style={styles.paginationNav}>
                      <TouchableOpacity
                        style={[styles.pageNavButton, !hasPrev && styles.pageNavButtonDisabled]}
                        onPress={() => hasPrev && setPage(page - 1)}
                        disabled={!hasPrev}
                      >
                        <Icon
                          name="chevron-left"
                          size={20}
                          color={hasPrev ? colors.primary : colors.textTertiary}
                        />
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.pageNavButton, !hasNext && styles.pageNavButtonDisabled]}
                        onPress={() => hasNext && setPage(page + 1)}
                        disabled={!hasNext}
                      >
                        <Icon
                          name="chevron-right"
                          size={20}
                          color={hasNext ? colors.primary : colors.textTertiary}
                        />
                      </TouchableOpacity>
                    </View>
                  </View>
                )}

                {/* AI Help section */}
                <View style={styles.aiHelpSection}>
                  <Icon name="robot-outline" size={24} color={colors.primary} />
                  <View style={styles.aiHelpContent}>
                    <Text style={styles.aiHelpTitle}>Besoin d'aide ?</Text>
                    <Text style={styles.aiHelpText}>
                      Demande à l'assistant IA d'analyser tes dépenses et d'optimiser ton budget.
                    </Text>
                  </View>
                  <TouchableOpacity
                    style={styles.aiHelpButton}
                    onPress={() => navigateTo('Chats')}
                  >
                    <Text style={styles.aiHelpButtonText}>Discuter</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </View>
        ) : (
          <View style={styles.tabContent}>
            {budgetLimits.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Icon name="chart-pie" size={64} color={colors.textSecondary} />
                <Text style={styles.emptyTitle}>Aucun plafond défini</Text>
                <Text style={styles.emptyText}>
                  Définis un plafond mensuel par catégorie ou par tag pour mieux contrôler tes
                  dépenses.
                </Text>
                <TouchableOpacity style={styles.emptyButton} onPress={handleAddLimit}>
                  <Icon name="plus" size={20} color={colors.primary} />
                  <Text style={styles.emptyButtonText}>Définir un plafond</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.limitsContainer}>
                {/* Kind filter chips */}
                <View style={styles.limitKindFilterRow}>
                  {(
                    [
                      { key: 'all', label: 'Tous' },
                      { key: 'category', label: 'Catégories' },
                      { key: 'tag', label: 'Tags' },
                    ] as { key: LimitKindFilter; label: string }[]
                  ).map((opt) => {
                    const active = limitKindFilter === opt.key;
                    return (
                      <TouchableOpacity
                        key={opt.key}
                        style={[styles.tagChip, active && styles.tagChipActive]}
                        onPress={() => setLimitKindFilter(opt.key)}
                      >
                        <Text style={[styles.tagChipText, active && styles.tagChipTextActive]}>
                          {opt.label}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>

                {filteredLimits.length === 0 ? (
                  <View style={styles.emptyLimitsForKind}>
                    <Text style={styles.emptyLimitsForKindText}>
                      {limitKindFilter === 'category'
                        ? 'Aucun plafond par catégorie.'
                        : 'Aucun plafond par tag.'}
                    </Text>
                  </View>
                ) : (
                  filteredLimits.map((bl) => (
                    <View key={bl.id}>
                      <BudgetLimitProgressCard
                        budgetLimit={bl}
                        onPress={() => handleEditLimit(bl)}
                      />
                      <TouchableOpacity
                        style={styles.deleteLimitButton}
                        onPress={() => handleDeleteLimit(bl)}
                      >
                        <Icon name="delete-outline" size={16} color={colors.error} />
                        <Text style={styles.deleteLimitText}>Supprimer</Text>
                      </TouchableOpacity>
                    </View>
                  ))
                )}

                <TouchableOpacity style={styles.addLimitButton} onPress={handleAddLimit}>
                  <Icon name="plus-circle-outline" size={24} color={colors.primary} />
                  <Text style={styles.addLimitText}>Ajouter un plafond</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}
      </ScrollView>

      {/* FAB - Add Transaction */}
      <TouchableOpacity
        style={styles.fab}
        onPress={() => {
          setEditingTransaction(null);
          setShowAddTransaction(true);
        }}
        activeOpacity={0.8}
      >
        <Icon name="plus" size={28} color="#FFFFFF" />
      </TouchableOpacity>

      {/* Modals */}
      <AddTransactionModal
        visible={showAddTransaction}
        onClose={handleCloseTransactionModal}
        transaction={editingTransaction}
      />

      <EditBudgetLimitModal
        visible={showEditLimit}
        onClose={() => {
          setShowEditLimit(false);
          setSelectedLimit(null);
          loadBudgetLimits();
        }}
        budgetLimit={selectedLimit}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollView: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
  },

  // Period inputs (Du / Au date fields inside the Filtres card)
  periodInputs: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: spacing.sm,
  },

  // Summary Card
  summaryCard: {
    backgroundColor: colors.background,
    borderRadius: 12,
    padding: spacing.md,
    marginHorizontal: spacing.lg,
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  summaryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  summaryTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
  },
  summaryStats: {
    marginBottom: spacing.sm,
  },
  summaryBudgetLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
  },
  summarySpentLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: 2,
  },
  progressContainer: {
    marginBottom: spacing.sm,
  },
  progressBar: {
    height: 8,
    backgroundColor: colors.border,
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  progressText: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  summaryActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  summaryActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: colors.primary + '10',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 6,
  },
  summaryActionText: {
    fontSize: typography.fontSize.xs,
    color: colors.primary,
    fontWeight: typography.fontWeight.medium as '500',
  },

  // Filters Card
  filtersCard: {
    backgroundColor: colors.background,
    borderRadius: 12,
    padding: spacing.md,
    marginHorizontal: spacing.lg,
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    zIndex: 100,
    position: 'relative',
  },
  filtersSectionTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.textSecondary,
    marginBottom: spacing.sm,
  },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs,
  },
  filterLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  filterLabelSmall: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
  },
  filterRowDouble: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.sm,
    zIndex: 50,
  },
  filterHalf: {
    flex: 1,
    zIndex: 50,
  },
  filterHalfOpen: {
    zIndex: 100,
  },
  selectWrapper: {
    position: 'relative',
    zIndex: 50,
  },
  accountFilterWrapper: {
    position: 'relative',
    zIndex: 60,
    marginBottom: spacing.sm,
  },
  filterSelect: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  filterSelectSmall: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  filterSelectText: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
    flex: 1,
  },
  filterSelectTextSmall: {
    fontSize: typography.fontSize.xs,
    color: colors.text,
    flex: 1,
  },
  filterDropdown: {
    position: 'absolute',
    top: 40,
    left: 0,
    right: 0,
    backgroundColor: colors.background,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    maxHeight: 200,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 999,
    zIndex: 9999,
  },
  filterDropdownSmall: {
    position: 'absolute',
    top: 30,
    left: 0,
    right: 0,
    backgroundColor: colors.background,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.border,
    maxHeight: 200,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 999,
    zIndex: 9999,
    overflow: 'visible',
  },
  filterOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  filterOptionActive: {
    backgroundColor: colors.primary + '10',
  },
  filterOptionContent: {
    flex: 1,
  },
  filterOptionText: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
  },
  filterOptionTextActive: {
    color: colors.primary,
    fontWeight: typography.fontWeight.semibold as '600',
  },
  filterOptionSubtext: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginTop: 2,
  },
  excludedRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.xs,
    marginBottom: spacing.sm,
  },
  excludedLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: spacing.sm,
    gap: spacing.xs,
  },
  searchInput: {
    flex: 1,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.sm,
    color: colors.text,
  },

  // Tag filter
  tagFilterScroll: {
    marginTop: spacing.xs,
    marginBottom: spacing.xs,
  },
  tagFilterContent: {
    gap: spacing.xs,
    paddingRight: spacing.sm,
  },
  tagChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: colors.backgroundSecondary,
    borderWidth: 1,
    borderColor: colors.border,
    flexDirection: 'row',
    alignItems: 'center',
  },
  tagChipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  tagChipText: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
  },
  tagChipTextActive: {
    color: '#fff',
    fontWeight: '600',
  },

  // Tabs
  tabsContainer: {
    flexDirection: 'row',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
    backgroundColor: colors.background,
  },
  tab: {
    flex: 1,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginHorizontal: spacing.xs,
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: colors.backgroundSecondary,
  },
  activeTab: {
    backgroundColor: colors.primary,
  },
  tabText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium as '500',
    color: colors.textSecondary,
  },
  activeTabText: {
    color: colors.background,
    fontWeight: typography.fontWeight.bold as '700',
  },

  // Error
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: colors.error + '10',
    marginHorizontal: spacing.lg,
    marginTop: spacing.sm,
    padding: spacing.sm,
    borderRadius: 8,
  },
  errorText: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
    flex: 1,
  },

  // Tab content
  tabContent: {
    paddingBottom: 100,
  },

  // Transactions Header — stacked layout for mobile responsiveness
  transactionsHeader: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    gap: spacing.sm,
  },
  transactionsHeaderActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  headerActionButton: {
    flex: 1,
    minWidth: 140,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: colors.primary + '10',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.primary + '30',
  },
  headerActionText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontWeight: typography.fontWeight.semibold as '600',
  },

  // Selected account badge
  selectedAccountBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.primary + '10',
    marginHorizontal: spacing.lg,
    marginBottom: spacing.sm,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 6,
  },
  selectedAccountText: {
    fontSize: typography.fontSize.xs,
    color: colors.primary,
    fontWeight: typography.fontWeight.medium as '500',
  },

  // Empty state
  emptyContainer: {
    alignItems: 'center',
    padding: spacing.xl,
    marginTop: spacing.xxl,
  },
  emptyTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  emptyText: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: spacing.lg,
  },
  emptyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    gap: spacing.sm,
  },
  emptyButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium as '500',
    color: colors.primary,
  },

  // Transactions
  transactionsContainer: {
    paddingHorizontal: spacing.lg,
  },
  dateGroup: {
    marginBottom: spacing.md,
  },
  dateGroupLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.textSecondary,
    marginBottom: spacing.sm,
  },
  transactionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  transactionItemSelected: {
    backgroundColor: colors.primary + '08',
  },
  checkboxContainer: {
    paddingRight: spacing.sm,
  },
  transactionIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  transactionInfo: {
    flex: 1,
  },
  transactionDescription: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium as '500',
    color: colors.text,
  },
  excludedBadge: {
    fontSize: typography.fontSize.xs,
    color: colors.textTertiary,
    fontStyle: 'italic',
  },
  transactionMeta: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: 2,
  },
  transactionAccount: {
    color: colors.textTertiary,
  },
  transactionAmount: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold as '700',
    marginRight: spacing.xs,
  },
  incomeValue: {
    color: colors.success,
  },
  expenseValue: {
    color: colors.error,
  },

  // Pagination footer — Qonto-exact layout
  paginationBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.sm,
    marginTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.borderLight,
  },
  perPageGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  perPageButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  perPageButton: {
    minWidth: 32,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  perPageButtonActive: {
    backgroundColor: colors.primary + '15',
  },
  perPageButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontWeight: typography.fontWeight.medium as '500',
  },
  perPageButtonTextActive: {
    color: colors.primary,
    fontWeight: typography.fontWeight.bold as '700',
  },
  perPageLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  paginationInfo: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  paginationNav: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  pageNavButton: {
    width: 32,
    height: 32,
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pageNavButtonDisabled: {
    opacity: 0.3,
  },

  // AI Help
  aiHelpSection: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary + '08',
    borderRadius: 12,
    padding: spacing.md,
    marginTop: spacing.lg,
    gap: spacing.sm,
    borderWidth: 1,
    borderColor: colors.primary + '20',
  },
  aiHelpContent: {
    flex: 1,
  },
  aiHelpTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
    marginBottom: 2,
  },
  aiHelpText: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    lineHeight: 16,
  },
  aiHelpButton: {
    backgroundColor: colors.primary,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
  },
  aiHelpButtonText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: '#FFFFFF',
  },

  // Limits (plafonds)
  limitsContainer: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
    gap: spacing.md,
  },
  limitKindFilterRow: {
    flexDirection: 'row',
    gap: spacing.xs,
    paddingBottom: spacing.xs,
  },
  emptyLimitsForKind: {
    paddingVertical: spacing.lg,
    alignItems: 'center',
  },
  emptyLimitsForKindText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontStyle: 'italic',
  },
  addLimitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    paddingVertical: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderStyle: 'dashed',
    gap: spacing.sm,
  },
  addLimitText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium as '500',
    color: colors.primary,
  },
  deleteLimitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingTop: spacing.xs,
    paddingRight: spacing.xs,
    gap: 4,
  },
  deleteLimitText: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
  },

  // Subtotal bar
  subtotalBar: {
    backgroundColor: colors.primary + '10',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 8,
    marginBottom: spacing.sm,
    marginHorizontal: spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  subtotalText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
    flex: 1,
  },
  subtotalIncome: {
    color: colors.success,
    fontWeight: typography.fontWeight.bold as '700',
  },
  subtotalExpense: {
    color: colors.error,
    fontWeight: typography.fontWeight.bold as '700',
  },
  subtotalAction: {
    fontSize: typography.fontSize.xs,
    color: colors.primary,
    textDecorationLine: 'underline',
  },

  // FAB
  fab: {
    position: 'absolute',
    right: spacing.lg,
    bottom: spacing.lg,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 6,
    zIndex: 10,
  },
});
